import { Questionnaire } from '../../types/fhir';
import { getTherapyMaterialDocument, parseTherapyMaterialPayload, TherapyMaterialDocument } from './therapyMaterial';

const WORKFLOW_CONTEXT_SYSTEM = 'http://terminology.hl7.org/CodeSystem/usage-context-type';
const SUPPORTED_CONTENT_TYPES = new Set([
    'header',
    'paragraph',
    'display',
    'image',
    'check',
    'listitem',
    'chat',
    'textfield',
    'imageform',
    'thoughtclouds',
]);
const TEXT_CONTENT_TYPES = new Set(['header', 'paragraph', 'display', 'check', 'listitem', 'chat', 'textfield']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const decodeJson = (rawPayload: string): unknown => {
    let parsed: unknown = rawPayload;

    for (let depth = 0; depth < 3 && typeof parsed === 'string'; depth += 1) {
        parsed = JSON.parse(parsed);
    }

    return parsed;
};

const hasTherapyMaterialUseContext = (questionnaire: Record<string, unknown>): boolean => {
    if (!Array.isArray(questionnaire.useContext)) {
        return false;
    }

    return questionnaire.useContext.some((entry) => {
        if (!isRecord(entry) || !isRecord(entry.code) || !isRecord(entry.valueCodeableConcept)) {
            return false;
        }
        if (entry.code.system !== WORKFLOW_CONTEXT_SYSTEM || entry.code.code !== 'workflow') {
            return false;
        }

        const concept = entry.valueCodeableConcept;
        if (concept.text === 'therapy-material') {
            return true;
        }

        return (
            Array.isArray(concept.coding) &&
            concept.coding.some((coding) => isRecord(coding) && coding.code === 'therapy-material')
        );
    });
};

const validateDocument = (document: TherapyMaterialDocument): void => {
    // The patient app requires the female content array and optionally switches to male content.
    if (!document.content.female?.length) {
        throw new Error('Therapy material must contain a non-empty female content array');
    }

    (['female', 'male'] as const).forEach((audience) => {
        document.content[audience]?.forEach((item) => {
            if (!SUPPORTED_CONTENT_TYPES.has(item.type)) {
                throw new Error(`Unsupported therapy material block: ${item.type}`);
            }
            if (TEXT_CONTENT_TYPES.has(item.type) && (typeof item.text !== 'string' || !item.text.trim())) {
                throw new Error(`Therapy material ${item.type} block must contain text`);
            }
            if (item.path !== undefined && typeof item.path !== 'string') {
                throw new Error('Therapy material image path must be a string');
            }
            if ((item.type === 'image' || item.type === 'imageform') && !item.path?.trim()) {
                throw new Error(`Therapy material ${item.type} block must contain an image path`);
            }
            if (item.w !== undefined && (!Number.isInteger(item.w) || item.w <= 0)) {
                throw new Error('Therapy material image width must be a positive integer');
            }
            if (item.h !== undefined && (!Number.isInteger(item.h) || item.h <= 0)) {
                throw new Error('Therapy material image height must be a positive integer');
            }
            if (item.rows !== undefined && (!Number.isInteger(item.rows) || item.rows <= 0)) {
                throw new Error('Therapy material image form rows must be a positive integer');
            }
            if (
                item.type === 'thoughtclouds' &&
                (!item.path?.trim() ||
                    !item.prompts?.length ||
                    !item.prompts.every((prompt) => typeof prompt === 'string' && prompt.trim()))
            ) {
                throw new Error('Therapy material thought clouds must contain an image path and prompts');
            }
        });
    });
};

const createQuestionnaireFromDocument = (
    document: TherapyMaterialDocument,
    serializedDocument: string,
): Questionnaire => {
    const textFields = document.content.female?.filter((item) => item.type === 'textfield') ?? [];

    return {
        resourceType: 'Questionnaire',
        status: 'draft',
        title: document.title,
        subjectType: ['Patient'],
        useContext: [
            {
                code: {
                    system: WORKFLOW_CONTEXT_SYSTEM,
                    code: 'workflow',
                },
                valueCodeableConcept: {
                    coding: [{ code: 'therapy-material' }],
                    text: 'therapy-material',
                },
            },
        ],
        item: [
            {
                linkId: document.id,
                type: 'display',
                text: serializedDocument,
            },
            ...textFields.map((item, index) => ({
                linkId: `${document.id}-tf-${index}`,
                type: 'text',
                text: item.text,
                required: false,
            })),
        ],
    };
};

/**
 * Verifies an uploaded therapy-material JSON file and returns the FHIR resource
 * used for both the read-only preview and the eventual API upload.
 *
 * Raw therapy material documents are converted to the Questionnaire envelope
 * expected by Qoala. Already-wrapped FHIR Questionnaires are accepted only when
 * they are explicitly marked as therapy material.
 */
export const prepareTherapyMaterialUpload = (rawPayload: string): Questionnaire => {
    const parsed = decodeJson(rawPayload);

    if (!isRecord(parsed)) {
        throw new Error('Invalid therapy material JSON');
    }

    if (parsed.resourceType === 'Questionnaire') {
        if (!hasTherapyMaterialUseContext(parsed)) {
            throw new Error('Questionnaire is not marked as therapy material');
        }

        const questionnaire = parsed as unknown as Questionnaire;
        const document = getTherapyMaterialDocument(questionnaire);
        validateDocument(document);
        return questionnaire;
    }

    const serializedDocument = JSON.stringify(parsed);
    const document = parseTherapyMaterialPayload(serializedDocument);
    validateDocument(document);
    return createQuestionnaireFromDocument(document, serializedDocument);
};
